import * as THREE from 'three';
import ThreeDScene from '../components/ThreeDScene';
import WsManagement from './WsManagement';
import { SyntheticMap, Vec2, createSyntheticMap } from '../models/SyntheticMap';
import { BaseActor } from '../components/threeDComponents/behaviors/BaseActor';
import { loadOrgRecords, saveModel } from './connection';
import { Asset } from '../models/Asset';
import { User } from '../models/User';
import { Org } from '../models/Org';
import { Deployment } from '../models/Deployment';
import { showSnackbar, showUniversalDialog, UniversalDialogType } from '../components/UniversalDialog';
import { MapService } from './MapService';
import { SimClient } from './simulation/SimClient';
import { BaseThreeDFactory } from '../components/threeDComponents/BaseThreeDFactory';

export interface ObjectClickAction {
  label: string;
  action: (loc: THREE.Vector3) => Promise<void>;
  cancelAction?: () => void;
  confirmAction?: () => void;
  path?: THREE.Vector3[];
  category?: string;
  holdSelection?: boolean;
}

export interface MenuClickAction {
  label: string;
  action: () => void;
}

export interface LoadableUIFactory {
  loadComponent(ctx: ComponentContext): Promise<void>;
  unloadComponent(): void;
  onMapLoad(): Promise<void>;
  onMapSave(): void;
  renderMap(ctx: CanvasRenderingContext2D, resolution: number, convertToMapCoordinates: (x: number, y: number) => Vec2): void;
}

export interface ComponentContext {
  renderer: any;
  camera: any;
  scene: ThreeDScene;
  wsManagement: WsManagement;
}

export enum UIServiceEvents {
  UIControlState = 'UIControlState',
}


export class UIService {
  public simMode: boolean = true;
  public scene!: ThreeDScene;
  public showGrid!: boolean;
  public isDraggingObject: boolean = false;
  public isInDeploymentMode: boolean = false;
  public activeDeployment: Deployment | null = null;
  public user!: User;
  public org!: Org;
  public isAltKeyDown = false;
  public mapService!: MapService;
  public isPlaying: boolean = false;
  public showBoundingBox: boolean = false;
  public selectedObject: THREE.Object3D | null = null;

  public setup(wsManagement: WsManagement) {
    this.wsManagement = wsManagement;
  }
  public updateUIState(dgram: Partial<UIService> = {}) {
    Object.assign(this, dgram);
    this.wsManagement.emit(UIServiceEvents.UIControlState, dgram);
  }
  public async loadComponent(comp: LoadableUIFactory, scene: ThreeDScene) {
    await comp.loadComponent({
      scene, wsManagement: this.wsManagement,
      renderer: undefined,
      camera: undefined
    });
    this.components.push(comp);
    this.scene = scene;
  }
  public clearComponents() {
    this.components.forEach((comp) => comp.unloadComponent());
    this.components = [];
    this.addObjectActions = [];
    this.environmentActions = [];
  }
  public async loadMap(id: string) {
    try {
      this.updateUIState({ mapIsLoading: true });
      await this.wsManagement.mapService.loadMap(id);
      for (const comp of this.components) {
        try {
          await comp.onMapLoad();
        } catch (e) {
          console.error({ failedToLoadComponentFromMap: comp, e });
        }
      }
      this.updateUIState({ map: this.wsManagement.mapService.map });
      this.scene.onMapLoad(this.wsManagement.mapService.map);
      this.updateUIState({ mapIsLoading: false, showGrid: this.wsManagement.uiService.map.settings.showGrid });
    } catch (e) {
      console.error(e);
      this.updateUIState({ mapIsLoading: false });
    }
  }

  public async getSimClient() {
    if (!this.wsManagement.simClient) {
      this.wsManagement.simClient = new SimClient(this);
    }
    return this.wsManagement.simClient;
  }

  public async selectMap() {
    this.updateUIState({ mapIsLoading: true });

    try {
      await this.stopSimClientIfRunning();

      const maps = await this.wsManagement.mapService.loadAllMaps();
      console.log(maps.map(m => ({ id: m._id, name: m.name })));
      if (maps.length === 0) {
        showSnackbar('No maps found', 'error');
        return null;
      }

      const sortedMaps = maps.sort((a, b) => a.name.localeCompare(b.name));
      const selectedMapName = await this.promptUserToSelectMap(sortedMaps);

      if (!selectedMapName) return null;

      const selectedMap = sortedMaps.find(map => map.name === selectedMapName);
      if (!selectedMap) {
        showSnackbar('Invalid map selected', 'error');
        return null;
      }

      await this.loadMap(selectedMap._id);
      this.updateUIState({ map: selectedMap });
      this.scene.onMapLoad(selectedMap);
      this.wsManagement.mapService.map = selectedMap;
      // window.history.pushState({}, '', `/sim/${selectedMap._id}`);
      return selectedMap;

    } catch (error) {
      console.error('Error loading maps:', error);
      showSnackbar('Failed to load maps', 'error');
      return null;
    } finally {
      this.updateUIState({ mapIsLoading: false });
    }
  }

  private async stopSimClientIfRunning() {
    const simClient = this.wsManagement.simClient;
    if (!simClient?.simConnection) return;

    try {
      const wsState = simClient.simConnection.getWebsocketState();
      if (wsState === 1) {
        console.log('Stopping simClient...');
        await simClient.stop();
        this.updateUIState({ isPlaying: false });
      }
    } catch (error) {
      console.error('Error stopping simClient:', error);
    }
  }

  private async promptUserToSelectMap(maps: any[]): Promise<string | null> {
    const mapList = maps.map(map => map.name).join('\n');
    return await new Promise((resolve) => {
      showUniversalDialog(
        'Select a Scene',
        mapList,
        UniversalDialogType.List,
        (yes, selectedName) => {
          resolve(yes ? selectedName : null);
        }
      );
    });
  }

  public async saveMap(isNew: boolean = false) {
    this.updateUIState({ mapIsLoading: true });
    for (const comp of this.components) {
      const saved = comp.onMapSave();
      if (saved === null) {
        console.error('Failed to save component:', comp);
        this.updateUIState({ mapIsLoading: false });
        return null;
      }
    }
    this.scene.onMapSave(this.wsManagement.mapService.map);
    await this.wsManagement.mapService.saveMap(isNew);
    this.updateUIState({ mapIsLoading: false });
  }



  public async newMap() {
    this.updateUIState({ mapIsLoading: true });
    this.wsManagement.mapService.map = createSyntheticMap();
    this.scene.onMapLoad(this.wsManagement.mapService.map);
    for (const comp of this.components) {
      await comp.onMapLoad();
    }
    this.updateUIState({ map: this.wsManagement.mapService.map });
    this.updateUIState({ mapIsLoading: false });
  }

  // public selectObject(e: InteractionEvent, component: BaseThreeDFactory) {
  //   const dgram = { selectionEvent: e, selectedHandlerComponent: component };
  //   this.updateUIState(dgram);
  // }



  // public selectObject(e: InteractionEvent, component: BaseThreeDFactory) {
  //   this.selectedObject = e.object; // Store the selected object
  //   this.selectedHandlerComponent = component;
  //   const dgram = { selectionEvent: e, selectedHandlerComponent: component };
  //   this.updateUIState(dgram);
  // }

  // public deselectObject() {
  //   this.selectedObject = null; // Clear selected object
  //   this.selectedHandlerComponent = null; // Clear component
  //   const dgram = { selectionEvent: null, selectedHandlerComponent: null };
  //   this.updateUIState(dgram);
  // }

  public getSelectedObject(): THREE.Object3D | null {
    return this.selectedObject;
  }

  public get map(): SyntheticMap {
    return this.wsManagement.mapService.map;
  }

  public set map(map: SyntheticMap) {
    this.wsManagement.mapService.map = map;
  }

  public async loadAssets() {
    const res = await loadOrgRecords<Asset>("assets");
    const sortedAssets = res.sort((a, b) => a.name.localeCompare(b.name));
    return sortedAssets;
  }

  public getFactory<T extends BaseThreeDFactory>(type: new () => T): T {
    const factory = this.components.find((f) => f instanceof type) as T;
    if (!factory) {
      throw new Error(`Factory not found: ${type.name}`);
    }
    return factory;
  }


  public async saveSlamMap() {
    // const mapChannel = robotActor.robocoreClient.getChannel('map') as MapChannel;
    // if (!mapChannel) {
    //   showSnackbar('Map channel not found', 'error');
    //   return;
    // }
    if (!this.activeDeployment) {
      showSnackbar('No active deployment', 'error');
      return;
    }
    // const latestMap = mapChannel.lastReceivedMap;
    // if (!latestMap) {
    //   showSnackbar('No map received', 'error');
    //   return;
    // }
    // const blobName = `${this.activeDeployment._id}_${robotActor.id}_${new Date().toISOString()}.png`;
    // await uploadBlob('techmage-slam-maps', blobName, latestMap.data!);
    // const slamMap = { ...latestMap };
    // delete slamMap.data;
    // slamMap.storageBlobId = blobName;
    const updatedDeployment: Deployment = {
      ...this.activeDeployment, slamMaps: [...this.activeDeployment.slamMaps,
        // slamMap as RobotSlamMap
      ]
    };
    await saveModel(updatedDeployment);
    showSnackbar('Slam map saved', 'success');
  }

  private wsManagement!: WsManagement;
  public environmentActions: MenuClickAction[] = [];
  public floorTextureActions: MenuClickAction[] = [];
  public addObjectActions: ObjectClickAction[] = [];
  public components: LoadableUIFactory[] = [];
  public pendingFloorClickAction?: ObjectClickAction | null = null;
  public pendingAddObjectAction?: ObjectClickAction | null = null;
  public mapIsLoading: boolean = false;
  public fpvCameraActor: BaseActor | null = null;
}
